import { Link } from "react-router-dom";
import { Check, ArrowUpRight } from "@phosphor-icons/react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import LeadSection from "@/components/sections/LeadSection";
import EditorialMarquee from "@/components/Marquee";
import FurnaceSentinelDemo from "@/components/sections/FurnaceSentinelDemo";
import CTASection from "@/components/sections/CTASection";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { SERVICE_PAGES, SERVICES } from "@/data/content";

export default function ServicePage({ slug }) {
  const cfg = SERVICE_PAGES[slug];
  const related = SERVICES.filter((s) => s.slug !== slug).slice(0, 3);
  const commercial = ["light-commercial-hvac", "rtu-change-outs"].includes(slug);
  const showSentinel = ["preventive-maintenance", "technology-diagnostics"].includes(slug);

  return (
    <>
      <Seo title={`${cfg.title} | Genuine Forge — Chicago HVAC`} description={`${cfg.lede} ${cfg.seo}.`} />
      <PageHero eyebrow={cfg.eyebrow} title={cfg.title} lede={cfg.lede} image={cfg.image} />

      {cfg.blocks.map((b, i) => (
        <section key={i} data-testid={`service-block-${i}`} className={`border-t border-white/10 py-20 md:py-28 ${i % 2 ? "bg-panel/40" : ""}`}>
          <div className="mx-auto grid max-w-[1440px] gap-12 px-6 md:px-12 lg:grid-cols-[1fr_1fr] lg:items-start">
            <Reveal>
              <h2 className="max-w-xl font-display text-3xl font-extrabold leading-tight tracking-tight md:text-4xl">{b.h}</h2>
              <p className="mt-6 max-w-xl font-body text-base leading-relaxed text-slatex-300">{b.p}</p>
            </Reveal>
            {b.list.length > 0 && (
              <Stagger className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2">
                {b.list.map((item) => (
                  <StaggerItem key={item} className="flex items-start gap-3 bg-ink p-5">
                    <Check size={14} weight="bold" className="mt-1 shrink-0 text-brand-cyan" />
                    <span className="font-body text-sm text-white">{item}</span>
                  </StaggerItem>
                ))}
              </Stagger>
            )}
          </div>
        </section>
      ))}

      <EditorialMarquee text="Engineered Reliability · Measured Diagnostics · Clean Installations · Clear Documentation ·" speed={22} />

      {showSentinel && <FurnaceSentinelDemo />}


      {/* related services */}
      <section className="border-t border-white/10 py-20 md:py-28">
        <div className="mx-auto max-w-[1440px] px-6 md:px-12">
          <Reveal>
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-brand-cyan">Related Services</span>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight md:text-4xl">Explore the full capability.</h2>
          </Reveal>
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {related.map((r) => (
              <Link key={r.slug} to={`/${r.slug}`} data-testid={`related-${r.slug}`} className="group flex flex-col justify-between border border-white/10 bg-panel p-8 transition-colors hover:border-brand-cyan/40">
                <div>
                  <span className="font-mono text-xs text-brand-cyan">{r.index}</span>
                  <h3 className="mt-3 font-display text-xl font-bold">{r.title}</h3>
                  <p className="mt-2 font-body text-sm text-slatex-300">{r.short}</p>
                </div>
                <span className="mt-6 flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-white/70 transition-colors group-hover:text-brand-cyan">
                  View <ArrowUpRight size={13} weight="bold" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <LeadSection commercial={commercial} sourcePage={slug} />
      <CTASection />
    </>
  );
}
